import React, {
  ComponentType,
  forwardRef,
  ForwardRefExoticComponent,
  JSX,
  KeyboardEvent,
  MouseEvent,
  Ref,
  useRef,
  useState
} from 'react';
import DatePicker, { DatePickerRef } from './DatePicker';
import useAdvancedFeatures, { getMonthIndex, getYear } from './useAdvancedFeatures';
import dayjs from 'dayjs';
import { useMount } from 'react-use';
import localeData from 'dayjs/plugin/localeData';
import { CustomsProps, DatePickerOnChange, DatePickerProps } from './DatePickerInModal';
import { OnChangeDate, OnChangeDateIntersection } from './CustomDisplayProps';
import DataTestIdProp from '../DataTestIdProp';

dayjs.extend(localeData);

export type ComponentProps = DatePickerProps &
  DataTestIdProp &
  CustomsProps & { ref: Ref<DatePickerRef> };

function withAdvancedFeatures(
  WrappedComponent: ForwardRefExoticComponent<ComponentProps>
): ComponentType<ComponentProps> {
  const AdvancedFeaturesComponent = (props: ComponentProps): JSX.Element => {
    const {
      onCalendarOpen,
      onCalendarClose,
      onMonthChange,
      onYearChange,
      onChange,
      date,
      startDate,
      endDate,
      excludeDates
    } = props;
    const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
    const [currentlyOpenMonth, setCurrentlyOpenMonth] = useState<number>(0);
    const [currentlyOpenYear, setCurrentlyOpenYear] = useState<number>(0);
    const [currentDate, setCurrentDate] = useState<OnChangeDate | Date[]>(null);
    const [isDateSelected, setIsDateSelected] = useState<boolean>(false);
    const datePickerWrapperRef = useRef<HTMLDivElement | null>(null);

    useAdvancedFeatures(
      isCalendarOpen,
      currentlyOpenMonth,
      currentlyOpenYear,
      currentDate as OnChangeDate,
      isDateSelected,
      datePickerWrapperRef,
      excludeDates
    );

    useMount(() => {
      const initialDate =
        !!startDate && !!endDate
          ? ([startDate, endDate] as [Date | null, Date | null])
          : startDate || date || new Date();
      setCurrentDate(initialDate);
      setCurrentlyOpenMonth(
        dayjs(Array.isArray(initialDate) ? initialDate[0] : initialDate).get('month')
      );
      setCurrentlyOpenYear(
        dayjs(Array.isArray(initialDate) ? initialDate[0] : initialDate).get('year')
      );
    });

    const setActiveMonthAndYearOnCalendarOpen = (): void => {
      if (datePickerWrapperRef.current) {
        const months = datePickerWrapperRef.current.querySelectorAll(
          '.react-datepicker__month-container'
        );
        if (months[0]) {
          setCurrentlyOpenMonth(getMonthIndex(months[0]));
          setCurrentlyOpenYear(getYear(months[0]));
        }
      }
    };

    const handleCalendarOpen = (): void => {
      setActiveMonthAndYearOnCalendarOpen();
      setIsCalendarOpen(true);
      if (onCalendarOpen) {
        onCalendarOpen();
      }
    };

    const handleCalendarClose = (): void => {
      setIsCalendarOpen(false);
      if (onCalendarClose) {
        onCalendarClose();
      }
    };

    const handleYearChange = (newDate: Date): void => {
      setCurrentlyOpenYear(dayjs(newDate).get('year'));
      if (onYearChange) {
        onYearChange(newDate);
      }
    };

    const handleMonthChange = (newDate: Date): void => {
      setCurrentlyOpenMonth(dayjs(newDate).get('month'));
      if (onMonthChange) {
        onMonthChange(newDate);
      }
      handleYearChange(newDate);
    };

    const handleChange = (
      newDate: OnChangeDate,
      event?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
    ): void => {
      setCurrentDate(newDate);
      if (onChange) {
        onChange(newDate as OnChangeDateIntersection, event);
      }
      if (!isDateSelected) {
        setIsDateSelected(true);
      }
    };

    return (
      <div ref={datePickerWrapperRef} className="react-datepicker__advanced d-inline">
        <WrappedComponent
          {...props}
          onCalendarOpen={handleCalendarOpen}
          onCalendarClose={handleCalendarClose}
          onMonthChange={handleMonthChange}
          onYearChange={handleYearChange}
          onChange={handleChange}
          focusSelectedMonth={false}
        />
      </div>
    );
  };

  AdvancedFeaturesComponent.displayName = `withAdvancedFeatures(${
    WrappedComponent.displayName || WrappedComponent.name || 'WrappedComponent'
  })`;

  return AdvancedFeaturesComponent;
}

const DatePickerAdvanced = withAdvancedFeatures(DatePicker);

export const DatePickerComponent: ForwardRefExoticComponent<
  DatePickerProps &
    DataTestIdProp &
    CustomsProps & {
      ref?: Ref<DatePickerRef>;
      onChange: DatePickerOnChange;
    } & { useAdvancedFeatures?: boolean }
> = forwardRef(
  (props, ref: Ref<DatePickerRef>): JSX.Element => {
    const { useAdvancedFeatures: doUseAdvancedFeatures, onChange } = props;
    return doUseAdvancedFeatures ? (
      <DatePickerAdvanced {...props} onChange={onChange} ref={ref} />
    ) : (
      <DatePicker {...props} onChange={onChange} ref={ref} />
    );
  }
);
